"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowUpLeft, Search } from "lucide-react";
import { ProductImage } from "./product-image";
import { money } from "@/lib/config";
import { normalizeSearchText } from "@/lib/search-text";
import type { ProductSearchResult } from "@/lib/product-search";

export function HeaderSearch({ compact = false }: { compact?: boolean }) {
  const path = usePathname();
  return <HeaderSearchForm key={path ?? "/"} compact={compact} />;
}

function HeaderSearchForm({ compact }: { compact: boolean }) {
  const root = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [compactOpen, setCompactOpen] = useState(false);
  const [matches, setMatches] = useState<ProductSearchResult[]>([]);
  const [searchState, setSearchState] = useState<"idle" | "loading" | "ready" | "unavailable">("idle");
  const [resultsQuery, setResultsQuery] = useState("");
  const normalized = normalizeSearchText(query);
  const displayedMatches = resultsQuery === normalized ? matches : [];
  const displayedState = resultsQuery === normalized ? searchState : "loading";

  useEffect(() => {
    if (!open || normalized.length < 2) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearchState("loading");
      try {
        const response = await fetch(`/api/search-products?q=${encodeURIComponent(normalized)}`, { signal: controller.signal });
        if (!response.ok) throw new Error("Product search unavailable");
        const data = await response.json() as { results?: ProductSearchResult[] };
        setMatches(Array.isArray(data.results) ? data.results : []);
        setResultsQuery(normalized);
        setSearchState("ready");
      } catch {
        if (!controller.signal.aborted) {
          setMatches([]);
          setResultsQuery(normalized);
          setSearchState("unavailable");
        }
      }
    }, 180);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [normalized, open]);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
        setCompactOpen(false);
      }
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setCompactOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, []);
  return <form ref={root} action="/products" className={`header-search${compactOpen ? " is-compact-open" : ""}`} role="search" onSubmit={() => { setOpen(false); setCompactOpen(false); }}>
    {compact && <button
      className="header-search-toggle"
      type="button"
      aria-label={compactOpen ? "بستن جست‌وجو" : "باز کردن جست‌وجو"}
      aria-expanded={compactOpen}
      onClick={() => {
        const next = !compactOpen;
        setCompactOpen(next);
        if (next) window.requestAnimationFrame(() => inputRef.current?.focus());
        else setOpen(false);
      }}
    ><Search size={20} aria-hidden="true" /></button>}
    <Search size={19} aria-hidden="true" />
    <input
      ref={inputRef}
      name="q"
      type="search"
      value={query}
      onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
      onFocus={() => setOpen(true)}
      aria-label="جست‌وجوی محصولات"
      aria-controls={open && normalized.length >= 2 ? "header-search-preview" : undefined}
      aria-busy={displayedState === "loading"}
      autoComplete="off"
      placeholder="دنبال چه چیزی می‌گردید؟"
    />
    <button type="submit" aria-label="جست‌وجو"><ArrowUpLeft size={19} /></button>
    {open && normalized && <div id="header-search-preview" className="header-search-preview" role="region" aria-live="polite" aria-label="پیش‌نمایش جست‌وجو">
      <strong className="header-search-preview-title">نتیجه‌های نزدیک</strong>
      {normalized.length < 2 ? <p role="status" className="header-search-empty">برای جست‌وجو دست‌کم دو نویسه بنویسید.</p>
        : displayedState === "loading" || displayedState === "idle" ? <p role="status" className="header-search-empty">در حال جست‌وجو…</p>
        : displayedState === "unavailable" ? <p role="status" className="header-search-empty">جست‌وجو موقتاً در دسترس نیست؛ دوباره تلاش کنید.</p>
        : displayedMatches.length ? <>
        {displayedMatches.map((product) => <Link key={product.slug} href={`/products/${product.slug}`} onClick={() => setOpen(false)} className="header-search-result">
          <span className="header-search-result-image"><ProductImage src={product.image} alt="" sizes="48px" /></span>
          <span className="header-search-result-name">{product.name}</span>
          <span className="header-search-result-price">{product.price ? money(product.price) : ""}</span>
        </Link>)}
        <button className="header-search-all" type="submit">دیدن همهٔ نتیجه‌ها <ArrowUpLeft size={16} /></button>
      </> : <p role="status" className="header-search-empty">محصولی با این عبارت پیدا نشد.</p>}
    </div>}
  </form>;
}
